import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Avatar,
  Pagination,
  Chip,
  Button,
  Autocomplete,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { adminEmailAPI, EmailUser } from "../../services/adminApi";

// ── Brand tokens (matches AdminLayout blue theme) ────────────────────────────
const C = {
  green: "#0A1628",         // navy header / buttons
  greenMid: "#1565C0",      // Dr.Paw blue
  greenLight: "#42A5F5",    // blue light
  accent: "#42A5F5",
  bg: "#F0F7FF",
  text: "#0A1628",
  muted: "#78909C",
  border: "rgba(21,101,192,0.1)",
  selectedBg: "rgba(21,101,192,0.06)",
  selectedBorder: "#1565C0",
};

// ── Debounce hook ────────────────────────────────────────────────────────────
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ── Component ────────────────────────────────────────────────────────────────
const AdminEmailBlast: React.FC = () => {
  const [mode, setMode] = useState<0 | 1>(0);

  // Bulk state
  const [users, setUsers] = useState<EmailUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selected, setSelected] = useState<Map<string, EmailUser>>(new Map());

  // Single mode state
  const [acOptions, setAcOptions] = useState<EmailUser[]>([]);
  const [acInput, setAcInput] = useState("");
  const [acLoading, setAcLoading] = useState(false);
  const [singleUser, setSingleUser] = useState<EmailUser | null>(null);

  // Composer state
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ sent: number; failed: number } | null>(null);
  const [sendError, setSendError] = useState("");

  const debouncedSearch = useDebounce(search, 300);
  const debouncedAcInput = useDebounce(acInput, 300);
  const prevAcInput = useRef("");

  // ── Bulk: fetch users ──────────────────────────────────────────────────────
  useEffect(() => {
    if (mode !== 0) return;
    setLoadingUsers(true);
    adminEmailAPI
      .listUsers({ search: debouncedSearch, page, limit: 20 })
      .then((res) => {
        setUsers(res.data.data);
        setTotalPages(res.data.pagination.pages);
        setTotalUsers(res.data.pagination.total);
      })
      .catch(() => {})
      .finally(() => setLoadingUsers(false));
  }, [mode, debouncedSearch, page]);

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // ── Single: autocomplete fetch ─────────────────────────────────────────────
  useEffect(() => {
    if (mode !== 1 || debouncedAcInput === prevAcInput.current) return;
    prevAcInput.current = debouncedAcInput;
    if (!debouncedAcInput.trim()) { setAcOptions([]); return; }
    setAcLoading(true);
    adminEmailAPI
      .listUsers({ search: debouncedAcInput, limit: 10 })
      .then((res) => setAcOptions(res.data.data))
      .catch(() => {})
      .finally(() => setAcLoading(false));
  }, [mode, debouncedAcInput]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const recipients: { email: string; name: string }[] =
    mode === 0
      ? Array.from(selected.values()).map((u) => ({ email: u.email, name: u.name }))
      : singleUser
      ? [{ email: singleUser.email, name: singleUser.name }]
      : [];

  const toggleRow = useCallback((user: EmailUser) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(user._id)) next.delete(user._id);
      else next.set(user._id, user);
      return next;
    });
  }, []);

  const selectPage = useCallback(() => {
    setSelected((prev) => {
      const next = new Map(prev);
      users.forEach((u) => next.set(u._id, u));
      return next;
    });
  }, [users]);

  const clearSelection = useCallback(() => setSelected(new Map()), []);

  const removeRecipient = (email: string) => {
    if (mode === 0) {
      setSelected((prev) => {
        const next = new Map(prev);
        Array.from(next.entries()).forEach(([id, u]) => { if (u.email === email) next.delete(id); });
        return next;
      });
    } else {
      setSingleUser(null);
    }
  };

  const handleSend = async () => {
    if (!recipients.length || !subject.trim() || !body.trim()) return;
    setSending(true);
    setResult(null);
    setSendError("");
    try {
      const res = await adminEmailAPI.send({ recipients, subject, body });
      setResult(res.data);
    } catch (err: any) {
      setSendError(err?.response?.data?.message || "Failed to send emails.");
    } finally {
      setSending(false);
    }
  };

  const handleClear = () => {
    setSubject("");
    setBody("");
    setResult(null);
    setSendError("");
  };

  // ── Chip display (max 8 visible + "+N more") ───────────────────────────────
  const visibleChips = recipients.slice(0, 8);
  const extraCount = recipients.length - 8;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: "flex", gap: 2.5, flexDirection: { xs: "column", lg: "row" }, alignItems: "flex-start" }}>

      {/* ══ LEFT: Select Recipients ══════════════════════════════════════════ */}
      <Card
        elevation={0}
        sx={{
          width: { xs: "100%", lg: 460 },
          flexShrink: 0,
          border: `1px solid ${C.border}`,
          borderRadius: "14px",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box sx={{ bgcolor: C.green, px: 3, py: 2 }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem" }}>
            Select Recipients
          </Typography>
        </Box>

        {/* Mode toggle */}
        <Box sx={{ px: 2, pt: 1.5 }}>
          <Tabs
            value={mode}
            onChange={(_, v) => { setMode(v); setSelected(new Map()); setSingleUser(null); }}
            sx={{
              minHeight: 36,
              "& .MuiTab-root": { minHeight: 36, fontSize: "0.8rem", fontWeight: 600, textTransform: "none", color: C.muted },
              "& .Mui-selected": { color: C.green },
              "& .MuiTabs-indicator": { bgcolor: C.green, height: 2.5, borderRadius: 2 },
            }}
          >
            <Tab label="Bulk" />
            <Tab label="Single User" />
          </Tabs>
        </Box>

        <Box sx={{ px: 2, pb: 2 }}>
          {/* ── BULK MODE ── */}
          {mode === 0 && (
            <>
              {/* Search + actions */}
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1.5, mb: 1 }}>
                <TextField
                  size="small"
                  placeholder="Search name or email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: C.muted }} />
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearch("")}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                    sx: { fontSize: "0.85rem" },
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
              </Box>

              <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                <Button
                  size="small"
                  onClick={selectPage}
                  sx={{ fontSize: "0.75rem", color: C.greenLight, textTransform: "none", p: "2px 8px" }}
                >
                  Select page
                </Button>
                {selected.size > 0 && (
                  <Button
                    size="small"
                    onClick={clearSelection}
                    sx={{ fontSize: "0.75rem", color: C.muted, textTransform: "none", p: "2px 8px" }}
                  >
                    Clear
                  </Button>
                )}
              </Box>

              {/* Progress bar */}
              <Box sx={{ height: 2, mb: 0.5 }}>
                {loadingUsers && (
                  <LinearProgress sx={{ height: 2, bgcolor: "rgba(35,56,46,0.1)", "& .MuiLinearProgress-bar": { bgcolor: C.green } }} />
                )}
              </Box>

              {/* Table */}
              <TableContainer sx={{ maxHeight: 360, border: `1px solid ${C.border}`, borderRadius: "8px" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ bgcolor: C.bg, borderBottom: `1px solid ${C.border}` }}>
                        <Checkbox
                          size="small"
                          indeterminate={selected.size > 0 && selected.size < users.length}
                          checked={users.length > 0 && users.every((u) => selected.has(u._id))}
                          onChange={(e) => e.target.checked ? selectPage() : clearSelection()}
                          sx={{ "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: C.green } }}
                        />
                      </TableCell>
                      <TableCell sx={{ bgcolor: C.bg, fontWeight: 700, fontSize: "0.75rem", color: C.muted, borderBottom: `1px solid ${C.border}` }}>
                        User
                      </TableCell>
                      <TableCell sx={{ bgcolor: C.bg, fontWeight: 700, fontSize: "0.75rem", color: C.muted, borderBottom: `1px solid ${C.border}` }}>
                        Email
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => {
                      const isSelected = selected.has(user._id);
                      return (
                        <TableRow
                          key={user._id}
                          onClick={() => toggleRow(user)}
                          sx={{
                            cursor: "pointer",
                            bgcolor: isSelected ? C.selectedBg : "transparent",
                            borderLeft: isSelected ? `3px solid ${C.selectedBorder}` : "3px solid transparent",
                            "&:hover": { bgcolor: isSelected ? C.selectedBg : "rgba(0,0,0,0.02)" },
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              sx={{ "&.Mui-checked": { color: C.green } }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar
                                src={user.avatar}
                                sx={{ width: 26, height: 26, fontSize: "0.7rem", bgcolor: C.greenMid }}
                              >
                                {user.name.charAt(0).toUpperCase()}
                              </Avatar>
                              <Typography sx={{ fontSize: "0.82rem", color: C.text, fontWeight: 500 }}>
                                {user.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: "0.78rem", color: C.muted }}>{user.email}</Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!loadingUsers && users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 4, color: C.muted, fontSize: "0.82rem" }}>
                          No users found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination footer */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1.5 }}>
                <Typography sx={{ fontSize: "0.75rem", color: C.muted }}>
                  {selected.size > 0
                    ? `${selected.size} selected · ${totalUsers} total`
                    : `${totalUsers} users`}
                </Typography>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  size="small"
                  shape="rounded"
                  sx={{
                    "& .MuiPaginationItem-root": { fontSize: "0.75rem" },
                    "& .Mui-selected": { bgcolor: `${C.green} !important`, color: "#fff" },
                  }}
                />
              </Box>
            </>
          )}

          {/* ── SINGLE MODE ── */}
          {mode === 1 && (
            <Box sx={{ mt: 1.5 }}>
              <Autocomplete
                options={acOptions}
                getOptionLabel={(o) => `${o.name} <${o.email}>`}
                inputValue={acInput}
                onInputChange={(_, v) => setAcInput(v)}
                onChange={(_, v) => { if (v) { setSingleUser(v); setAcInput(""); setAcOptions([]); } }}
                loading={acLoading}
                filterOptions={(x) => x}
                noOptionsText={acInput.length < 2 ? "Type to search…" : "No users found"}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar src={option.avatar} sx={{ width: 28, height: 28, fontSize: "0.75rem", bgcolor: C.greenMid }}>
                      {option.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>{option.name}</Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: C.muted }}>{option.email}</Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search user…"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18, color: C.muted }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {acLoading && <CircularProgress size={14} sx={{ color: C.green, mr: 1 }} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                )}
              />

              <Box sx={{ mt: 2 }}>
                {singleUser ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: "10px",
                      border: `1px solid ${C.selectedBorder}`,
                      bgcolor: C.selectedBg,
                    }}
                  >
                    <Avatar src={singleUser.avatar} sx={{ width: 36, height: 36, bgcolor: C.greenMid }}>
                      {singleUser.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: C.text }}>
                        {singleUser.name}
                      </Typography>
                      <Chip
                        label={singleUser.email}
                        size="small"
                        sx={{ fontSize: "0.72rem", height: 20, mt: 0.3, bgcolor: C.greenMid, color: "#fff" }}
                      />
                    </Box>
                    <IconButton size="small" onClick={() => setSingleUser(null)}>
                      <CloseIcon sx={{ fontSize: 16, color: C.muted }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      p: 3,
                      borderRadius: "10px",
                      border: `2px dashed ${C.border}`,
                      color: C.muted,
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 32, color: C.accent }} />
                    <Typography sx={{ fontSize: "0.82rem" }}>Search and select a user above</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Card>

      {/* ══ RIGHT: Compose Message ════════════════════════════════════════════ */}
      <Card
        elevation={0}
        sx={{
          flex: 1,
          minWidth: 0,
          border: `1px solid ${C.border}`,
          borderRadius: "14px",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box sx={{ bgcolor: C.green, px: 3, py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EmailIcon sx={{ color: "rgba(255,255,255,0.7)", fontSize: 18 }} />
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem" }}>
              Compose Message
            </Typography>
          </Box>
        </Box>

        {/* Alert */}
        {(result || sendError) && (
          <Box sx={{ px: 2.5, pt: 2 }}>
            {result && (
              <Alert
                severity={result.failed === 0 ? "success" : "warning"}
                onClose={() => setResult(null)}
                sx={{ fontSize: "0.82rem" }}
              >
                {result.sent} sent{result.failed > 0 ? `, ${result.failed} failed` : " successfully"}.
              </Alert>
            )}
            {sendError && (
              <Alert severity="error" onClose={() => setSendError("")} sx={{ fontSize: "0.82rem" }}>
                {sendError}
              </Alert>
            )}
          </Box>
        )}

        {/* Composer */}
        <Box>
          {/* To row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              px: 2.5,
              py: 1.5,
              borderBottom: `1px solid ${C.border}`,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontSize: "0.8rem", color: C.muted, fontWeight: 600, pt: "2px", minWidth: 24 }}>
              To:
            </Typography>
            {recipients.length === 0 ? (
              <Typography sx={{ fontSize: "0.82rem", color: C.muted, fontStyle: "italic" }}>
                No recipients selected
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, flex: 1 }}>
                {visibleChips.map((r) => (
                  <Chip
                    key={r.email}
                    label={r.name || r.email}
                    size="small"
                    onDelete={() => removeRecipient(r.email)}
                    sx={{
                      fontSize: "0.75rem",
                      height: 24,
                      bgcolor: C.selectedBg,
                      border: `1px solid ${C.selectedBorder}`,
                      color: C.text,
                      "& .MuiChip-deleteIcon": { fontSize: 14, color: C.muted },
                    }}
                  />
                ))}
                {extraCount > 0 && (
                  <Chip
                    label={`+${extraCount} more`}
                    size="small"
                    sx={{ fontSize: "0.75rem", height: 24, bgcolor: C.greenMid, color: "#fff" }}
                  />
                )}
              </Box>
            )}
          </Box>

          {/* Subject row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2.5,
              py: 1.2,
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <Typography sx={{ fontSize: "0.8rem", color: C.muted, fontWeight: 600, flexShrink: 0 }}>
              Subject:
            </Typography>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter subject…"
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                fontSize: "0.875rem",
                color: C.text,
                background: "transparent",
                fontFamily: "inherit",
              }}
            />
          </Box>

          {/* Body */}
          <Box sx={{ px: 2.5, pt: 1.5 }}>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your message here…"
              rows={14}
              style={{
                width: "100%",
                border: "none",
                outline: "none",
                resize: "vertical",
                minHeight: 280,
                fontSize: "0.875rem",
                color: C.text,
                lineHeight: 1.7,
                background: "transparent",
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />
          </Box>

          {/* Action bar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2.5,
              py: 1.5,
              bgcolor: "rgba(35,56,46,0.04)",
              borderTop: `1px solid ${C.border}`,
            }}
          >
            <Typography sx={{ fontSize: "0.75rem", color: C.muted }}>
              {body.length} characters
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                onClick={handleClear}
                disabled={sending}
                sx={{ fontSize: "0.8rem", color: C.muted, textTransform: "none" }}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSend}
                disabled={sending || recipients.length === 0}
                startIcon={
                  sending ? (
                    <CircularProgress size={14} sx={{ color: "inherit" }} />
                  ) : (
                    <SendIcon sx={{ fontSize: 16 }} />
                  )
                }
                sx={{
                  bgcolor: C.green,
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 2,
                  "&:hover": { bgcolor: C.greenMid },
                  "&.Mui-disabled": { bgcolor: "rgba(35,56,46,0.3)", color: "rgba(255,255,255,0.5)" },
                }}
              >
                {sending
                  ? "Sending…"
                  : recipients.length > 0
                  ? `Send to ${recipients.length} recipient${recipients.length > 1 ? "s" : ""}`
                  : "Send Email"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default AdminEmailBlast;
